// ADR 6.10.26a §5 — `?install=1`, on any route.
//
// A link (the public site's install CTA, 6.10.26b's handoff) asks the app to
// open its install on arrival. The canonical entry is `/vocab?install=1`, but
// the reader is route-agnostic: the flag counts at top level on any path, or
// inside the `next` parameter the signed-out rewrite writes
// (`/signin?next=%2F%3Finstall%3D1`, `app/signedOutEntry.ts`). The pathname is
// never consulted, and `next` is only read for one flag, never followed.
//
// Pure over a search string, so the whole contract is a unit test.
// `startInstallCapture` reads it once at boot; the install strip strips it
// once the request opens.
//
// ADR 6.10.26b §1 — `entryFlag` is the one reader of the entry query: its
// `handoff=missing` reads through it too, and its handoff URL is built from
// `INSTALL_ENTRY_PATH` and `INSTALL_PARAM`.

/** The canonical install entry's path (6.10.26a), which a handoff lands on. */
export const INSTALL_ENTRY_PATH = '/vocab'
export const INSTALL_PARAM = 'install'
const NEXT = 'next'

/** `next`'s query, without its path or hash, or `null` when it carries none. */
function nextQuery(params: URLSearchParams): string | null {
  const next = params.get(NEXT)
  if (next === null) return null
  const start = next.indexOf('?')
  if (start === -1) return null
  const hash = next.indexOf('#', start)
  return next.slice(start + 1, hash === -1 ? undefined : hash)
}

/**
 * The top-level-then-`next` lookup, written once: `entryFlag` exposes it, and
 * 6.10.26b reads its `handoff` flag through that.
 */
function flagValue(search: string, name: string): string | null {
  try {
    const params = new URLSearchParams(search)
    const top = params.get(name)
    if (top !== null) return top
    const inner = nextQuery(params)
    return inner === null ? null : new URLSearchParams(inner).get(name)
  } catch {
    return null
  }
}

/** True when `name=value` is carried at top level or inside a decoded `next`. */
export function entryFlag(search: string, name: string, value: string): boolean {
  return flagValue(search, name) === value
}

/** True when `install=1` is carried at top level or inside a decoded `next`. */
export function installRequested(search: string): boolean {
  return entryFlag(search, INSTALL_PARAM, '1')
}

/** `next` with `install` dropped from its query, path and hash kept. */
function nextWithoutInstall(next: string): string {
  const start = next.indexOf('?')
  if (start === -1) return next
  const hashAt = next.indexOf('#', start)
  const hash = hashAt === -1 ? '' : next.slice(hashAt)
  const inner = new URLSearchParams(next.slice(start + 1, hashAt === -1 ? undefined : hashAt))
  inner.delete(INSTALL_PARAM)
  const query = inner.toString()
  return `${next.slice(0, start)}${query === '' ? '' : `?${query}`}${hash}`
}

/**
 * The search with `install` removed at top level and from inside `next`
 * (re-encoded); every other parameter kept. `''` when nothing is left.
 */
export function withoutInstall(search: string): string {
  const params = new URLSearchParams(search)
  params.delete(INSTALL_PARAM)
  const next = params.get(NEXT)
  if (next !== null) params.set(NEXT, nextWithoutInstall(next))
  const query = params.toString()
  return query === '' ? '' : `?${query}`
}
